import { Fragment } from "react"
import type { FC, ReactElement } from "react"

import map from "lodash-es/map"
import { Navigate, Outlet, Route, Routes } from "react-router-dom"

import { Loadable } from "@/3514/components/shared/loadable/loadable.component"
import {
    ASSISTANT_ROOT_PATH,
    COACH_OR_PM_ROOT_PATH,
    ERoute,
    PARTICIPANT_ONBOARDING_ROOT_PATH,
    PARTICIPANT_ROOT_PATH
} from "@/3514/constants"
import { type TUserState, useUser } from "@/3514/hooks"
import { AssistantLayout, AuthLayout, CoachLayout, ParticipantLayout } from "@/3514/layouts"
import {
    AssistantParticipantSessionsPage,
    AssistantParticipantsPage,
    AssistantSwapSessionPage,
    AuthLoginPage,
    AuthRegisterPage,
    AuthResetPassPage,
    CoachCalendarAccountsPage,
    CoachCalendarAvailabilityPage,
    CoachCalendarSetupPage,
    CoachDashboardPage,
    CoachGroupPage,
    CoachGroupParticipantPage,
    CoachIndividualParticipantPage,
    CoachRelinkAccountsPage,
    ParticipantChapterPage,
    ParticipantCoachingSessionsPage,
    ParticipantDashboardPage,
    ParticipantDiagnosticSummaryPage
} from "@/3514/pages"
import {
    ParticipantOnboardingLayout,
    participantOnboardingPageRenderer,
    participantOnboardingPages
} from "@/3514/pages/participant.onboarding"
import { isUserAssistant, isUserCoach, isUserManager, isUserParticipant } from "@/3514/utils"
import WaitingListPage from "@/components/waiting-list/WaitingListPage"
import { INDEX_URL, ParticipantUrls } from "@/constants"
import { PrivacyPolicyPage } from "@/pages"
import { coachRoutes, participantRoutes, unauthorizedPages } from "@/routes"
import ProtectedRoute from "@/routes/protected-route"
import { ForbiddenComponent } from "@/shared/error-page/ForbiddenComponent"
import { NotFoundPage } from "@/shared/error-page/NotFoundPage"
import PageLoader from "@/shared/spinner/PageLoader"

import { RootLoaderFallback } from "./root-loader.component"

const AppRouter: FC = (): ReactElement => {
    const { user, isLoading, isLoaded }: TUserState = useUser()

    return (
        <Routes>
            <Route path={INDEX_URL} element={<ProtectedRoute user={user} isAllowed isLoading={isLoading} />} />

            <Route
                key={ERoute.Register}
                path={ERoute.Register}
                element={
                    <AuthLayout>
                        <AuthRegisterPage />
                    </AuthLayout>
                }
            />

            <Route
                key={ERoute.Login}
                path={ERoute.Login}
                element={
                    <AuthLayout>
                        <AuthLoginPage />
                    </AuthLayout>
                }
            />

            <Route
                key={ERoute.ResetPassword}
                path={ERoute.ResetPassword}
                element={
                    <AuthLayout>
                        <AuthResetPassPage />
                    </AuthLayout>
                }
            />

            <Route
                path={ERoute.Participant_Dashboard}
                element={!user ? <ProtectedRoute user={user} isAllowed isLoading={false} /> : <ParticipantLayout />}
            >
                <Route
                    index
                    element={
                        <ProtectedRoute
                            redirectPath="/forbidden"
                            isAllowed={isLoaded && isUserParticipant(user)}
                            user={user}
                            isLoading={isLoading}
                        >
                            <ParticipantDashboardPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ERoute.Participant_DashboardModule}
                    element={
                        <ProtectedRoute
                            redirectPath="/forbidden"
                            isAllowed={isLoaded && isUserParticipant(user)}
                            user={user}
                            isLoading={isLoading}
                        >
                            <ParticipantDashboardPage />
                        </ProtectedRoute>
                    }
                />
            </Route>

            <Route
                path={PARTICIPANT_ROOT_PATH}
                element={
                    <ProtectedRoute
                        redirectPath="/forbidden"
                        isAllowed={isLoaded && isUserParticipant(user)}
                        user={user}
                        isLoading={isLoading}
                        loadingFallback={<RootLoaderFallback />}
                    >
                        <Outlet />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to={INDEX_URL} replace />} />
                <Route index path="*" element={<Navigate to={INDEX_URL} replace />} />

                <Route
                    path={ERoute.Participant_DiagnosticSummary}
                    element={
                        <ProtectedRoute
                            isAllowed={isLoaded && isUserParticipant(user)}
                            user={user}
                            isLoading={isLoading}
                            loadingFallback={<RootLoaderFallback />}
                        >
                            <ParticipantDiagnosticSummaryPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path={PARTICIPANT_ONBOARDING_ROOT_PATH}
                    element={
                        <ProtectedRoute
                            redirectPath="/forbidden"
                            isAllowed={isLoaded && isUserParticipant(user)}
                            user={user}
                            isLoading={isLoading}
                            loadingFallback={<RootLoaderFallback />}
                        >
                            <ParticipantOnboardingLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<Navigate to={ERoute.Participant_OnboardingRedirect} replace />} />

                    <Route path="*" element={<Navigate to={ERoute.Participant_OnboardingRedirect} replace />} />

                    {map(participantOnboardingPages, page => (
                        <Route
                            key={page.path}
                            path={page.path}
                            element={
                                <ProtectedRoute
                                    isAllowed={isLoaded && isUserParticipant(user)}
                                    user={user}
                                    isLoading={isLoading}
                                    loadingFallback={<RootLoaderFallback />}
                                >
                                    {participantOnboardingPageRenderer({
                                        page: page.component,
                                        isLoaded,
                                        isLoading
                                    })}
                                </ProtectedRoute>
                            }
                        />
                    ))}
                </Route>
            </Route>

            <Route
                path={ASSISTANT_ROOT_PATH}
                element={
                    <ProtectedRoute
                        redirectPath="/forbidden"
                        isAllowed={isLoaded && isUserAssistant(user)}
                        user={user}
                        isLoading={isLoading}
                        loadingFallback={<RootLoaderFallback />}
                    >
                        <AssistantLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to={ERoute.Assistant_Participants} replace />} />

                <Route path="*" element={<Navigate to={ERoute.Assistant_Participants} replace />} />

                <Route
                    path={ERoute.Assistant_Participants}
                    element={
                        <ProtectedRoute
                            isAllowed={isLoaded && isUserAssistant(user)}
                            user={user}
                            isLoading={isLoading}
                            loadingFallback={<RootLoaderFallback />}
                        >
                            <Loadable
                                loadingFallback={<RootLoaderFallback />}
                                isLoading={isLoading}
                                isReady={isLoaded && isUserAssistant(user)}
                                component={AssistantParticipantsPage}
                            />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path={ERoute.Assistant_ParticipantSessions}
                    element={
                        <ProtectedRoute
                            isAllowed={isLoaded && isUserAssistant(user)}
                            user={user}
                            isLoading={isLoading}
                            loadingFallback={<RootLoaderFallback />}
                        >
                            <Loadable
                                loadingFallback={<RootLoaderFallback />}
                                isLoading={isLoading}
                                isReady={isLoaded && isUserAssistant(user)}
                                component={AssistantParticipantSessionsPage}
                            />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path={ERoute.Assistant_SwapSession}
                    element={
                        <ProtectedRoute
                            isAllowed={isLoaded && isUserAssistant(user)}
                            user={user}
                            isLoading={isLoading}
                            loadingFallback={<RootLoaderFallback />}
                        >
                            <Loadable
                                loadingFallback={<RootLoaderFallback />}
                                isLoading={isLoading}
                                isReady={isLoaded && isUserAssistant(user)}
                                component={AssistantSwapSessionPage}
                            />
                        </ProtectedRoute>
                    }
                />
            </Route>

            <Route
                path={COACH_OR_PM_ROOT_PATH}
                element={
                    <ProtectedRoute
                        redirectPath="/forbidden"
                        isAllowed={isLoaded && (isUserCoach(user) || isUserManager(user))}
                        user={user}
                        isLoading={isLoading}
                        loadingFallback={<RootLoaderFallback top="25%" xsTop="45%" />}
                    >
                        <CoachLayout withHtmlScrollBarGutter />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to={ERoute.Coach_Dashboard} replace />} />

                <Route path="*" element={<Navigate to={ERoute.Coach_Dashboard} replace />} />

                <Route
                    path={ERoute.Coach_Dashboard}
                    element={
                        <ProtectedRoute
                            redirectPath={ERoute.Coach_Dashboard}
                            isAllowed={isLoaded && (isUserCoach(user) || isUserManager(user))}
                            user={user}
                            isLoading={isLoading}
                        >
                            <CoachDashboardPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    index
                    path={ERoute.Coach_Group}
                    element={
                        <ProtectedRoute
                            redirectPath={ERoute.Coach_Dashboard}
                            isAllowed={isLoaded && (isUserCoach(user) || isUserManager(user))}
                            user={user}
                            isLoading={isLoading}
                            loadingFallback={<RootLoaderFallback top="25%" xsTop="45%" />}
                        >
                            <CoachGroupPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    index
                    path={ERoute.Coach_GroupParticipant}
                    element={
                        <ProtectedRoute
                            redirectPath={ERoute.Coach_Dashboard}
                            isAllowed={isLoaded && (isUserCoach(user) || isUserManager(user))}
                            user={user}
                            isLoading={isLoading}
                            loadingFallback={<RootLoaderFallback top="25%" xsTop="45%" />}
                        >
                            <CoachGroupParticipantPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path={ERoute.Coach_IndividualParticipant}
                    element={
                        <ProtectedRoute
                            redirectPath={ERoute.Coach_Dashboard}
                            isAllowed={isLoaded && (isUserCoach(user) || isUserManager(user))}
                            user={user}
                            isLoading={isLoading}
                            loadingFallback={<RootLoaderFallback top="25%" xsTop="45%" />}
                        >
                            <CoachIndividualParticipantPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path={ERoute.Coach_CalendarSetup}
                    element={
                        <ProtectedRoute
                            redirectPath={ERoute.Coach_Dashboard}
                            isAllowed={isLoaded && isUserCoach(user)}
                            user={user}
                            isLoading={isLoading}
                            loadingFallback={<RootLoaderFallback top="10%" />}
                        >
                            <Loadable
                                isLoading={isLoading}
                                loadingFallback={<RootLoaderFallback top="10%" />}
                                component={CoachCalendarSetupPage}
                            />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path={ERoute.Coach_CalendarAvailability}
                    element={
                        <ProtectedRoute
                            redirectPath={ERoute.Coach_Dashboard}
                            isAllowed={isLoaded && isUserCoach(user)}
                            user={user}
                            isLoading={isLoading}
                            loadingFallback={<RootLoaderFallback top="10%" />}
                        >
                            <Loadable
                                isLoading={isLoading}
                                loadingFallback={<RootLoaderFallback top="10%" />}
                                component={CoachCalendarAvailabilityPage}
                            />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path={ERoute.Coach_CalendarAccounts}
                    element={
                        <ProtectedRoute
                            redirectPath={ERoute.Coach_Dashboard}
                            isAllowed={isLoaded && isUserCoach(user)}
                            user={user}
                            isLoading={isLoading}
                            loadingFallback={<RootLoaderFallback top="10%" />}
                        >
                            <Loadable
                                isLoading={isLoading}
                                loadingFallback={<RootLoaderFallback top="10%" />}
                                component={CoachCalendarAccountsPage}
                            />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path={ERoute.Coach_RelinkAccounts}
                    element={
                        <ProtectedRoute
                            redirectPath={ERoute.Coach_Dashboard}
                            isAllowed={isLoaded && isUserCoach(user)}
                            user={user}
                            isLoading={isLoading}
                            loadingFallback={<RootLoaderFallback top="10%" />}
                        >
                            <Loadable
                                isLoading={isLoading}
                                loadingFallback={<RootLoaderFallback top="10%" />}
                                component={CoachRelinkAccountsPage}
                            />
                        </ProtectedRoute>
                    }
                />
            </Route>

            <Route
                path={ERoute.Participant_Chapter}
                element={
                    !user ? (
                        <ProtectedRoute user={user} isAllowed isLoading={false} />
                    ) : (
                        <Loadable
                            component={<ParticipantLayout withHtmlScrollBarGutter />}
                            loadingFallback={<Fragment />}
                            isLoading={isLoading}
                        />
                    )
                }
            >
                <Route
                    path={ERoute.Participant_ChapterId}
                    element={
                        <ProtectedRoute
                            redirectPath="/forbidden"
                            isAllowed={isLoaded && isUserParticipant(user)}
                            user={user}
                            isLoading={isLoading}
                        >
                            <Loadable
                                isLoading={isLoading}
                                loadingFallback={<PageLoader />}
                                component={<ParticipantChapterPage />}
                            />
                        </ProtectedRoute>
                    }
                />
            </Route>

            <Route
                path={ERoute.Participant_CoachingSessions}
                element={
                    <ProtectedRoute
                        redirectPath="/forbidden"
                        isAllowed={isLoaded && isUserParticipant(user)}
                        user={user}
                        isLoading={isLoading}
                    >
                        <Loadable
                            isLoading={isLoading}
                            loadingFallback={<PageLoader />}
                            component={<ParticipantCoachingSessionsPage />}
                        />
                    </ProtectedRoute>
                }
            />

            {participantRoutes.map(({ path, Component }) => (
                <Route
                    key={path}
                    path={path}
                    element={
                        <ProtectedRoute
                            redirectPath="/forbidden"
                            isAllowed={isLoaded && isUserParticipant(user)}
                            user={user}
                            isLoading={isLoading}
                        >
                            <Loadable component={<Component />} loadingFallback={<PageLoader />} />
                        </ProtectedRoute>
                    }
                />
            ))}

            {coachRoutes.map(({ path, Component, ...rest }) => (
                <Route
                    key={path}
                    path={path}
                    element={
                        <ProtectedRoute
                            redirectPath="/forbidden"
                            isAllowed={isLoaded && (isUserCoach(user) || isUserManager(user))}
                            user={user}
                            isLoading={isLoading}
                        >
                            <Component {...rest} />
                        </ProtectedRoute>
                    }
                />
            ))}

            {unauthorizedPages.map(({ path, Component }) => (
                <Route path={path} element={<Component />} key={path} />
            ))}

            <Route path={ParticipantUrls.WAITING_LIST} element={<WaitingListPage />} />
            <Route path={ParticipantUrls.WAITING_LIST_SSO} element={<WaitingListPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />

            <Route
                path={ParticipantUrls.END_OF_JOURNEY_PAGE}
                element={<Navigate to={ParticipantUrls.OFFBOARDING} replace />}
            />

            <Route path="/forbidden" element={<ForbiddenComponent />} />

            <Route path="404" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />
        </Routes>
    )
}

AppRouter.displayName = "AppRouter"

export { AppRouter }
